import React, { useEffect } from "react";
import ReactDOM from "react-dom/client";
import "inticons/fonts/inticons.bundle.min.css";
import "inticons/fonts/inticons.untitled-ui.bundle.min.css";
import App from "./App";
import { RecoilRoot } from "recoil";
import { RouterProvider, createBrowserRouter, useLocation } from "react-router-dom";
import ErrorBoundary from "./ErrorBoundary";
import TagManager from "react-gtm-module";
import { QueryClient, QueryClientProvider } from "react-query";
import { ReactQueryDevtools } from "react-query/devtools";
import "./index.css";
import "smartbanner.js/dist/smartbanner.min.js";
import "smartbanner.js/dist/smartbanner.min.css";
import "./style/smartbanner-overrides.css";
import * as Sentry from "@sentry/react";
import { BrowserTracing } from "@sentry/browser";
import StyledToastContainer from "component/shared/StyledToastContainer";
import "react-toastify/dist/ReactToastify.css";
import AppHeader from "./component/header/AppHeader";
import BottomNavigation from "./component/BottomNavigation";
import { useRouteMeta } from "./hooks/useRouteMeta";
import { cls } from "./utils/util";
import Footer from "./component/footer/Footer";
import { initializeWebviewOverrides } from "./utils/webviewUtil";
import { isChunkLoadError, attemptChunkRecoveryReload } from "@/utils/lazyLoad";

if (import.meta.env.PROD) {
  Sentry.init({
    dsn: "https://2b0d3de374e14910be0c79b5bdacf3b7@o491558.ingest.sentry.io/5779872",
    integrations: [new BrowserTracing()],
    tracesSampleRate: 0.01,
  });
}

if (import.meta.env.VITE_APP_ARG === "prd") {
  const tagManagerArgs = {
    gtmId: "GTM-TQNF527",
  };
  TagManager.initialize(tagManagerArgs);
}

// webview 환경에서 window.open 오버라이드 초기화
initializeWebviewOverrides();

window.addEventListener("unhandledrejection", (event) => {
  if (isChunkLoadError(event.reason)) {
    event.preventDefault();
    attemptChunkRecoveryReload();
  }
});

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      cacheTime: 15 * 60 * 1000,
      staleTime: 0,
      retry: 0,
    },
  },
});

const AppWrapper = () => {
  const location = useLocation();
  const routeMeta = useRouteMeta();

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [location.pathname]);

  return (
    <RecoilRoot>
      <ErrorBoundary>
        <QueryClientProvider client={queryClient}>
          {!import.meta.env.PROD && <ReactQueryDevtools initialIsOpen={false} />}
          <div className={cls("flex flex-col", routeMeta?.bottomNavigation && "pb-[4rem] lg:pb-0")}>
            <AppHeader />
            <div className="flex-1 min-h-screen">
              <App />
            </div>
            {routeMeta?.bottomNavigation && <BottomNavigation />}
            <Footer />
          </div>
          <StyledToastContainer />
        </QueryClientProvider>
      </ErrorBoundary>
    </RecoilRoot>
  );
};

const router = createBrowserRouter([
  {
    path: "/*",
    element: <AppWrapper />,
  },
]);

ReactDOM.createRoot(document.getElementById("root")!).render(
  <React.StrictMode>
    <RouterProvider router={router} />
  </React.StrictMode>,
);
